import { Fragment } from 'react';
import { Calender } from '../common/Calender';
import { CheckInLoadingPopup } from '../common/CheckInLoadingPopup';
import { ErrorMessageModelOnSubmit } from '../common/ErrorMessageModelOnSubmit';
import { ErrorModel } from '../common/ErrorModel';
import { ResponseToast } from '../common/ResponseToast';
import { TaskDoneWarningModel } from '../common/TaskDoneWarningModel';
import { UnfreezeModal } from '../common/UnfreezeModal';
import { Banner } from '../layout/Banner';
import { Feedback } from '../layout/Feedback';
import { TimeEntryMainPage } from './TimeEntryMainPage';
import { TimeEntryRightPanel } from './TimeEntryRightPanel';
import { useAuth } from '../../context/AuthContext';
import {
  useTimeEntryController,
  type TimeEntryControllerProps,
} from '../../hooks/useTimeEntryController';
import warningIcon from '../../logo/warning.png';
import type { SelectedTaskValue } from '../../types';

/**
 * The render half of `mainMenu.jsx` — the week toolbar, the grid, the right panel, and the
 * five overlays (submit-validation modal, task-done warning, loading popup, response
 * toast, error modal).
 *
 * Every method that used to sit alongside this markup is in
 * `hooks/useTimeEntryController.ts`; see that file's header for why it is one hook rather
 * than the nine the plan sketched.
 *
 * `class=` is written as `className=` on the four buttons that used the raw attribute
 * (jsx:2431, :2448, :2474, :2486). React renders `class` either way, so the emitted DOM is
 * identical — this only silences a console warning.
 */

export type MainMenuProps = Omit<
  TimeEntryControllerProps,
  'showBannerInitially' | 'showFeedbackInitially'
>;

export function MainMenu(props: MainMenuProps): JSX.Element {
  const { config } = useAuth();

  const controller = useTimeEntryController({
    ...props,
    // Old: envConfig.banner.showBanner / envConfig.showFeedback, read in the constructor.
    showBannerInitially: config?.banner.showBanner ?? false,
    showFeedbackInitially: config?.feedback.showFeedback ?? false,
  });

  const { state, setState } = controller;
  const { bannerVisible, feedbackVisible } = state;

  return (
    <Fragment>
      {bannerVisible ? (
        <Banner
          bannerVisible={(value) => {
            setState({ bannerVisible: value });
          }}
        />
      ) : (
        <div>
          {feedbackVisible && (
            <Feedback
              hide={() => {
                setState({
                  toast: false,
                });
              }}
              feedbackVisible={(value) => {
                setState({
                  feedbackVisible: value,
                });
              }}
            />
          )}
        </div>
      )}
      <div className="ch-main-nav">
        <div className="week-nav">
          <button
            className="prev-week"
            disabled={
              !state.clickInput ||
              Number(state.selectedDate?.year) < state.selectedDate?.maxPastNavigateYear
            }
            onClick={() => {
              controller.pastWeek();
            }}
            title="Navigate to previous week"
          ></button>
          <button
            ref={controller.calendarRef}
            className="date"
            onClick={() => {
              controller.handleCalender();
            }}
            disabled={!state.clickInput}
            title="Navigate to Next/Previous Week By Using Calender Popup"
          >
            {' '}
            {state.selectedDate.dateOfFirstDayofCurrentWeek} {' to '}
            {state.selectedDate.dateOfLastDayofCurrentWeek}
            {state.openCalender && (
              <Calender
                getSelectedDate={(date) => {
                  setState({ datePicker: date });
                }}
                selectedWeekFirstDate={state.selectedDate.firstDayofCurrentWeek}
              />
            )}
          </button>
          <button
            className="next-week"
            tabIndex={0}
            disabled={!state.clickInput || state.disableNextBtn}
            onClick={() => {
              controller.nextWeek();
            }}
            title="Navigate to next week"
          ></button>
          <div
            className="week-no"
            tabIndex={0}
            title={` ${state.selectedDate.year} week ${state.selectedDate.week}`}
          >
            <b>
              {state.selectedDate.year} {' Week '}
            </b>
            {state.selectedDate.week}
          </div>

          {/*
            Disabled while the week IS frozen — the button raises a ServiceNow request to
            unfreeze it, so it is only offered once the freeze date has been read and the
            week is not covered by it.
          */}
          <button
            onClick={() => controller.unfreezeButton()}
            className="rounded-pill unfreez"
            disabled={state.freezeDateData?.isUpdated && state.freezeDateData?.isFreezed}
          >
            Unfreeze My Week
          </button>
          <button
            className="hcmAbsenceModule"
            onClick={() => window.open(config?.hcmAbsenceModuleUrl, '_blank')}
          >
            Apply Absence
          </button>
          <section>
            {state.unfreezeModal && (
              <UnfreezeModal
                selectedDate={state.selectedDate}
                showUnfreezeModal={(show) => setState({ unfreezeModal: show })}
                projectList={state.projectList as never}
                operationalUserGteEmployeeNo={state.operationalUserGteEmployeeNo}
                loggedUserGteEmployeeNo={state.loggedUserGteEmployeeNo}
                onError={(error) => setState({ error })}
              />
            )}
          </section>

          <button
            className="copy_Previous_Week"
            onClick={() => controller.copyWeekButton()}
            title="Copy Previous Week Billable Task Times to Current Week"
          >
            Copy Previous Week
          </button>
        </div>

        <div className="nav-actions">
          <button
            className="btn saveDraft"
            disabled={!state.clickInput}
            onClick={() => {
              controller.handleSaveDraft(
                true,
                false,
                state.userTimeSheetData,
                state.selectedDate.year,
                state.selectedDate.week,
                state.loggedUserGteEmployeeNo,
                state.operationalUserGteEmployeeNo,
              );
            }}
            data-toggle="tooltip"
            data-placement="left"
            title="Save as Draft"
            // `alt` is not valid on a <button>; React renders it anyway and the original
            // had it, so it is kept to match the DOM exactly.
            {...{ alt: 'Save as Draft' }}
            tabIndex={0}
          >
            Save Draft
          </button>

          <button
            tabIndex={0}
            className="btn submit"
            disabled={!state.clickInput}
            onClick={() => {
              controller.handleSubmitForApproval(
                state.userTimeSheetData,
                state.selectedDate.year,
                state.selectedDate.week,
                state.loggedUserGteEmployeeNo,
                state.operationalUserGteEmployeeNo,
              );
            }}
            data-toggle="tooltip"
            data-placement="left"
            title="Submit for Approval"
            {...{ alt: 'Submit for Approval' }}
          >
            Submit for Approval
          </button>
        </div>
      </div>
      <div className="ch-dynamic-container">
        <TimeEntryMainPage
          pastWeekLeaveMismatchesDetails={state.pastWeekLeaveMismatchesDetails}
          updateStateofIsTaskDone={(value) => {
            controller.updateStateofIsTaskDone(value);
          }}
          operationalUserGteEmployeeNo={props.operationalUserGteEmployeeNo}
          loggedUserGteEmployeeNo={props.loggedUserGteEmployeeNo}
          rightPanelDivClassNameProperty={state.rightPanelDivClassNameProperty}
          deleteTaskData={(res: SelectedTaskValue, gteProjectId: number) => {
            setState({
              deleteTaskData: res,
              deleteGteProjectId: gteProjectId,
            });
          }}
          freezeDateData={state.freezeDateData}
          selectedDate={state.selectedDate}
          selectedTaskValue={state.selectedTaskValue}
          projectList={state.projectList}
          userTimeSheetData={state.userTimeSheetData}
          clickInput={state.clickInput}
          isDisabled={!state.clickInput}
          loading={state.loading}
          addTimeEntered={(value, gteProjectId, task, timedate) => {
            controller.timSlotValueUpdate(value, gteProjectId, task, timedate, 'timeChange');
          }}
          validateTimeEntered={(value, gteProjectId, task, timedate) => {
            controller.timSlotValueUpdate(value, gteProjectId, task, timedate, 'timeValidation');
          }}
          setSelectedTaskPanelValue={(timeSheet, gteProjectId, task, timedate) => {
            controller.setSelectedTaskPanelValue(timeSheet, gteProjectId, task, timedate);
          }}
          updateStateOfUserTimeSheetData={(userTimeSheetData) => {
            controller.updateStateOfTimeSheetDataList(userTimeSheetData);
          }}
          updateStateOfProjectList={(projectList) => {
            setState({ projectList: projectList });
          }}
          updateStateOfRightPanelDivClassNameProperty={(rightPanelDivClassNameProperty) => {
            controller.updateStateOfRightPanelDivClassNameProperty(rightPanelDivClassNameProperty);
          }}
          refreshTimeSheetData={(selectedDate, empNo, projId) => {
            controller.refreshTimeSheetData(selectedDate, empNo, projId, false);
          }}
          updateSelectedTaskValue={(selectedTaskValue) => {
            controller.updateSelectedTaskValue(selectedTaskValue);
          }}
        />

        <TimeEntryRightPanel
          updateSelectedTaskValue={(selectedTaskValue) => {
            controller.updateSelectedTaskValue(selectedTaskValue);
          }}
          isCopyUnassigedTaskEnabled={state.isCopyUnassigedTaskEnabled}
          freezeDateData={state.freezeDateData}
          addTaskResponse={(response) => {
            controller.addTaskResponse(response);
          }}
          callUnassignTask={state.callUnassignTask}
          copyUnassignTaskButton={() => {
            controller.copyUnassignTaskButton();
          }}
          // null and undefined are interchangeable downstream — Alerts only ever reads
          // this through `alertData?.alerts?.length`.
          alertData={props.alertData ?? undefined}
          selectedDateYear={(date) => {
            setState({ datePicker: date });
          }}
          deleteTaskData={state.deleteTaskData}
          deleteGteProjectId={state.deleteGteProjectId as number}
          projectList={state.projectList}
          refreshTimeSheetData={(selectedDate, empNo, projId) => {
            controller.refreshTimeSheetData(selectedDate, empNo, projId, false);
          }}
          clickInput={state.clickInput}
          loggedUserDetails={props.loggedUserDetails}
          loggedUserGteEmployeeNo={state.loggedUserGteEmployeeNo}
          projectIdList={state.projectListIds}
          operationalUserGteEmployeeNo={props.operationalUserGteEmployeeNo}
          selectedDate={state.selectedDate}
          addTask={state.addTask}
          nonProjectTask={state.nonProjectTask}
          unassignedTasks={state.unassignedTasks}
          userTimeSheetData={state.userTimeSheetData}
          selectedTaskValue={state.selectedTaskValue}
          rightPanelDivClassNameProperty={state.rightPanelDivClassNameProperty}
          validateTimeEntered_TimeType={(value, selectedTaskValue, entry) =>
            controller.validateTimeEntered_TimeType(value, selectedTaskValue, entry)
          }
          addTimeEntered_TimeType={(value, selectedTaskValue, entry) =>
            controller.addTimeEntered_TimeType(
              value,
              selectedTaskValue,
              entry,
              false, // not check error in time entry
            )
          }
          highlightSelectedInputOfTimeType={(selectedTaskValue, entry) =>
            controller.highlightSelectedInputOfTimeType(selectedTaskValue, entry)
          }
          enterInvoiceCommentOfTimeTypeProject={(value, selectedTaskValue, entry) =>
            controller.enterInvoiceCommentOfTimeTypeProject(value, selectedTaskValue, entry)
          }
          handleSaveDraft={() =>
            controller.handleSaveDraft(
              false,
              false,
              state.userTimeSheetData,
              state.selectedDate.year,
              state.selectedDate.week,
              state.loggedUserGteEmployeeNo,
              state.operationalUserGteEmployeeNo,
            )
          }
          updateStateOfRightPanelDivClassNameProperty={(rightPanelDivClassNameProperty) => {
            controller.updateStateOfRightPanelDivClassNameProperty(rightPanelDivClassNameProperty);
          }}
        />
      </div>
      <section>
        {state.errorModel?.openErrorModel && (
          <ErrorMessageModelOnSubmit
            minimumHourExceedError={state.errorModel?.minimumHourExceedError}
            maximumHourExceedError={state.errorModel?.maximumHourExceedError}
            invoiceCommentMissingError={state.errorModel?.invoiceCommentMissingError}
            hide={() => {
              setState({
                errorModel: {
                  openErrorModel: false,
                  minimumHourExceedError: { error: false, errorList: [] },
                  maximumHourExceedError: { error: false, errorList: [] },
                  invoiceCommentMissingError: { error: false, errorList: [] },
                },
              });
            }}
          />
        )}
      </section>
      <section>
        {state.isTaskDone && (
          <TaskDoneWarningModel
            taskDone={(task) => {
              setState({ isTaskDone: task });
            }}
          />
        )}
      </section>
      <section>{state.loading && <CheckInLoadingPopup initialLoading={state.initialLoading} />}</section>
      <section>
        {state.response && (
          <ResponseToast
            responseMessage={
              <div style={{ textAlign: 'center' }}>
                {state.responseMessage}!
                {state.responseMessage === 'Time Save Successful' && (
                  <div>
                    {state.showWarningIcon && (
                      <img
                        src={warningIcon}
                        style={{
                          width: '25px',
                          height: '25px',
                          padding: '2px',
                          display: 'inline-block',
                        }}
                        alt="Warning Icon"
                      />
                    )}
                    {' Please Submit Time for Project Manager Approval.'}
                  </div>
                )}
              </div>
            }
            hide={() => {
              controller.setStateOfResponse(false);
            }}
          />
        )}
      </section>
      <section>
        <div>
          {state.error?.status && (
            <ErrorModel
              errorResponse={state.error}
              hide={() => {
                setState({
                  error: {
                    status: false,
                    message: '',
                  },
                });
              }}
            />
          )}
        </div>
      </section>
    </Fragment>
  );
}
